import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.concrete;

export const articleHowMuchConcreteNeed: Article = {
  slug: "how-much-concrete-do-i-need-guide",
  title: "How Much Concrete Do I Need? Calculating Cubic Yards and Bag Counts",
  description: "Concrete is ordered and calculated by cubic yard (27 cubic feet). Multiply length × width × thickness in feet, divide by 27, and add 10% for waste. Here's the full calculation with worked examples.",
  category: "Concrete",
  tags: ["Concrete", "DIY", "Materials", "Construction Basics"],
  createdAt: "June 22, 2026",
  updatedAt: "July 2, 2026",
  lastVerified: "July 2, 2026",
  readTime: "6 min read",
  keyTakeaway: "Concrete is ordered and calculated by volume, measured in cubic yards (27 cubic feet). To calculate the volume of a slab, multiply the length by the width by the thickness in feet, and divide by 27. Always add a 10% safety buffer to account for ground irregularities and form bowing.",
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "American Concrete Institute (ACI) - Section 301 Specifications for Structural Concrete",
      url: "https://www.concrete.org"
    },
    {
      name: "Portland Cement Association (PCA) - Concrete Calculation Standards",
      url: "https://www.cement.org"
    }
  ],
  keyMetrics: [
    { label: "Cubic Yard in Cubic Feet", value: "27 cu. ft." },
    { label: "80 lb Bag Yield", value: "0.60 cu. ft." },
    { label: "60 lb Bag Yield", value: "0.45 cu. ft." },
    { label: "Recommended Buffer", value: "+10% Waste" }
  ],
  faqs: [
    {
      question: "Should I order ready-mix or buy bags?",
      answer: "For slabs over 2 cubic yards (about 100 sq ft at 4 inches thick), ready-mix delivery is cheaper and faster. For smaller projects like post holes or stepping stones, 80 lb bags from the home center are more practical. Keep in mind: mixing 40+ bags by hand is exhausting work."
    },
    {
      question: "What happens if I order too little concrete?",
      answer: "You'll end up with a 'cold joint' where fresh concrete meets partially hardened concrete. This creates a structural weak point that can crack under load. It's always better to have a little leftover than to run short. Most contractors will donate or discard excess; you can't fix a short pour."
    }
  ],
  content: `
    <p>
      Concrete is ordered and sold by the cubic yard. One cubic yard equals 27 cubic feet, a cube measuring 3 feet on each side. Ready-mix trucks deliver in cubic yards; bagged mix yields fractions of a cubic foot per bag. To order correctly, you need the project volume in cubic yards before anything else (to automate these calculations, try our <a href="/calculators/concrete/" class="text-[var(--accent)] hover:underline font-semibold">concrete calculator</a>).
    </p>
    <p>
      The calculation is straightforward, but it has one consistent error point: thickness. Most homeowners measure thickness in inches but need to convert it to feet before multiplying. Mixing inches and feet produces a wrong number by a factor of 12.
    </p>

    <div class="my-6 p-4 rounded-lg bg-surface border border-border-custom">
      <p class="font-bold text-foreground m-0 mb-2">The Short Version</p>
      <p class="text-sm text-text-muted m-0">
        Concrete is calculated in cubic yards (27 cubic feet). For a slab: multiply length × width × thickness (in feet), then divide by 27. A 10×10 slab at 4 inches thick needs 1.23 cubic yards. Always add 10% extra for waste and ground irregularities. One 80 lb bag yields 0.60 cubic feet; you'll need 45 bags per cubic yard.
      </p>
    </div>


    <h2>The Core Concrete Formula</h2>
    <p>
      A cubic yard is simply a cube measuring 3 feet on all sides. That means it contains exactly 27 cubic feet.
    </p>
    <p>
      To find your project's volume in yards, you multiply the length, width, and thickness in feet, then divide that total by 27.
    </p>
    <p class="font-mono bg-surface-muted/50 p-4 border rounded border-border text-sm">
      Cubic Yards = [Length (feet) × Width (feet) × Thickness (feet)] ÷ 27
    </p>
    <p>
      The trickiest step for most homeowners is converting the slab's thickness from inches to feet. If you multiply feet by inches, your math will be completely off. Always divide the target inches by 12 first:
    </p>
    <ul>
      <li>4 inches of thickness = 4 ÷ 12 = 0.33 feet</li>
      <li>6 inches of thickness = 6 ÷ 12 = 0.50 feet</li>
    </ul>

    <!--COMPONENT:info-note|label="Meazora Tip"-->
    Always round UP when ordering concrete. If your calculation says 3.2 cubic yards, order 3.5 or even 4 yards. The cost of a few extra cubic feet is trivial compared to the disaster of running short. Excess concrete can be used for walkways or garden borders; a short pour creates a permanent structural weakness.
    <!--ENDOFCOMPONENT-->

    <h2>Bagged Mix vs. Ready-Mix: When to Use Which</h2>
    <p>
      <strong>Bagged Mix (60 lb or 80 lb bags):</strong> Best for small projects under 2 cubic yards. Post holes, stepping stones, small repairs, and garden borders. An 80 lb bag yields 0.60 cubic feet; a 60 lb bag yields 0.45 cubic feet. You'll need 45 bags of 80 lb mix to equal one cubic yard.
    </p>
    <p>
      <strong>Ready-Mix Delivery:</strong> Best for slabs, driveways (which should be sealed after curing; see our <a href="/articles/driveway-sealing-cost-guide/" class="text-[var(--accent)] hover:underline font-semibold">driveway sealing cost guide</a>), and large pads over 2 cubic yards. A standard ready-mix truck carries 8-10 cubic yards. For smaller orders (1-5 yards), expect a "short-load" fee of $15-$30 per yard under the minimum.
    </p>

    <h2>Ready-Mix Concrete Reinforcement</h2>
    <p>
      For structural durability, a concrete slab requires internal reinforcement to resist tension. While wire mesh prevents cosmetic surface cracking, steel rebar provides true structural capacity. Reinforcement must sit at the mid-depth of the slab supported by chairs, not on the ground. Refer to the reinforcement detail below.
    </p>

    <div class="my-6 border border-border-custom rounded-lg overflow-hidden bg-surface">
      <img src="/images/assets/concrete-reinforcement-detail.webp" alt="Concrete Slab Reinforcement Detail Diagram" class="w-full h-auto display-block" />
      <div class="p-4 bg-surface-muted border-t border-border-custom font-mono text-[10px]">
        <p class="font-bold text-foreground mb-1 uppercase">Share This Diagram on Your Site (Embed Code):</p>
        <textarea readonly class="w-full p-2 bg-surface text-text-muted rounded border border-border-custom h-16 resize-none focus:outline-none select-all" onclick="this.select()">&lt;a href="https://www.meazora.com/articles/how-much-concrete-do-i-need-guide/"&gt;&lt;img src="https://www.meazora.com/images/assets/concrete-reinforcement-detail.webp" alt="Concrete Slab Reinforcement Detail by Meazora" width="100%" border="0" /&gt;&lt;/a&gt;&lt;br/&gt;Source: &lt;a href="https://www.meazora.com/articles/how-much-concrete-do-i-need-guide/"&gt;Meazora Concrete Calculation Guide&lt;/a&gt;</textarea>
      </div>
    </div>

    <h2>Common Mistakes to Avoid</h2>
    <ul>
      <li><strong>Forgetting the waste buffer:</strong> Ground isn't perfectly flat. Forms bow outward under pressure. Always add 10% to your calculation.</li>
      <li><strong>Mixing inches and feet:</strong> If your slab is 4 inches thick, don't multiply by 4. Multiply by 0.33 (4 ÷ 12).</li>
      <li><strong>Ordering exact amounts:</strong> See above. Always round up. The $50 extra for half a yard is cheaper than a ruined project.</li>
      <li><strong>Not checking access:</strong> Ready-mix trucks are 8 feet wide and 12 feet tall. They need a clear path to within 10-15 feet of your pour site. If the truck can't reach, you'll be wheelbarrowing concrete, and that's exhausting work.</li>
    </ul>

    <h2>Example Calculations</h2>
    <p>
      <strong>10×10 Patio at 4 inches thick:</strong><br />
      (10 × 10 × 0.33) ÷ 27 = 1.22 cubic yards<br />
      With 10% buffer: 1.34 cubic yards (round up to 1.5 yards)<br />
      In bags: 1.34 × 27 ÷ 0.60 = 60 bags of 80 lb mix
    </p>
    <p>
      <strong>24×24 Driveway at 6 inches thick:</strong><br />
      (24 × 24 × 0.50) ÷ 27 = 10.67 cubic yards<br />
      With 10% buffer: 11.74 cubic yards (round up to 12 yards)<br />
      This requires a full ready-mix truck order.
    </p>

    <p>
      Access is often the overlooked constraint for ready-mix orders. Standard ready-mix trucks are 8 feet wide and 12 feet tall, and they need a clear path to within 10–15 feet of the pour site. If the truck can't get within range, the concrete must be wheelbarrowed to the forms, which adds labor, time, and the risk of concrete beginning to set during transport. Confirm site access before scheduling delivery. If access is limited, a smaller volumetric truck (which mixes on-site) may be available in your area.
    </p>
    <p>
      Use our <a href="/calculators/concrete/" class="text-accent underline decoration-accent/30 hover:decoration-accent/60 transition-colors">Concrete Calculator</a> to compute cubic yards, bag counts, and cost estimates for your specific dimensions.
    </p>
  `
};